import { asyncForEach, delay, getErrorMessage } from '@eigtech/function-utils'
import {
  DefaultError,
  FetchQueryOptions,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  ky,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@eigtech/ui-shared-api'
import { minutesToMilliseconds } from '@eigtech/ui-shared-dates'
import { chunk } from 'lodash-es'
import { useState } from 'react'
import { FileEntry } from '../types/file-entry'

const apiInstance = ky.create({
  timeout: minutesToMilliseconds(10),
})

export type GetUploadFileUrlResponse = {
  fields: Record<string, string>
  url: string
}

export type UseGetUploadFileUrlProps<TQueryKey extends QueryKey = QueryKey> = UseQueryOptions<
  GetUploadFileUrlResponse,
  DefaultError,
  GetUploadFileUrlResponse,
  TQueryKey
>

const getUploadFileUrlQueryOptions = <TQueryKey extends QueryKey = QueryKey>(
  queryProps: UseGetUploadFileUrlProps<TQueryKey>
) =>
  // ponytail: cast bridges queryOptions DataTag key vs fetchQuery's QueryKey expectation (TanStack Query v5)
  queryOptions({
    staleTime: 25 * 60 * 1000,
    ...queryProps,
  }) as FetchQueryOptions<
    GetUploadFileUrlResponse,
    DefaultError,
    GetUploadFileUrlResponse,
    QueryKey
  >

export function useGetUploadFileUrl<TQueryKey extends QueryKey = QueryKey>(
  queryProps: UseGetUploadFileUrlProps<TQueryKey>
) {
  return useQuery(getUploadFileUrlQueryOptions(queryProps))
}
export type FileUpload = {
  file: File | FileEntry
  meta?: Record<string, string>
  'Content-Type'?: string
}

export type UploadFileProps = {
  url: string
  fields: Record<string, string>
} & FileUpload

export async function uploadFile({ url, file, fields, meta = {}, ...other }: UploadFileProps) {
  const formData = new FormData()

  // The fields must be included in the request.
  // Since we can't omit them, '' (an empty string) has been designated as "fallback to default"
  // on the server side.
  // I'm also not totally sure what kinds of restrictions exist on these fields in terms of character count limit, encoding, etc.
  // We'll want to make sure that whatever limits do exist, we enforce them on the UI side.

  const normalizedMeta = Object.entries(meta).map(([key, value]) => [`x-amz-meta-${key}`, value])
  const allFields = [...normalizedMeta, ...Object.entries(other), ...Object.entries(fields)]

  allFields.forEach(([key, value]) => {
    formData.append(key, value)
  })

  const fileToUpload = file instanceof File ? file : file.getFile()

  if (!fileToUpload) {
    throw new Error('File not found')
  }

  formData.append('file', fileToUpload)

  try {
    if ('updateFile' in file) {
      file.updateFile({ ...file, status: 'uploading' })
    }

    const response = await apiInstance.post(url, { body: formData, timeout: 10 * 60 * 1000 })

    if ('updateFile' in file) {
      file.updateFile({ ...file, status: 'uploaded' })
    }

    if ('revokeFile' in file) {
      file.revokeFile()
    }

    return response
  } catch (error) {
    if ('updateFile' in file) {
      file.updateFile({ ...file, status: 'error' })
    }
    throw error
  }
}

export function useUploadFileMutation(
  options: UseMutationOptions<
    unknown,
    unknown,
    FileUpload & {
      uploadInfo: GetUploadFileUrlResponse
      defaultMeta?: Record<string, string>
    },
    unknown
  > = {}
) {
  const mutation = useMutation({
    ...options,
    async mutationFn({ defaultMeta, uploadInfo, meta, ...file }) {
      return uploadFile({
        ...uploadInfo,
        ...file,
        meta: { ...(defaultMeta ?? {}), ...(meta ?? {}) },
      })
    },
  })

  return mutation
}

export function useUploadFile<TQueryKey extends QueryKey = QueryKey>(
  props: UseGetUploadFileUrlProps<TQueryKey>,
  options: UseMutationOptions<unknown, unknown, FileUpload, unknown> & {
    defaultMeta?: Record<string, string>
  } = {}
) {
  const queryClient = useQueryClient()
  const { isPending, isError, isFetching } = useGetUploadFileUrl(props)

  const baseMutation = useUploadFileMutation(options)

  const mutation = useMutation({
    ...options,
    async mutationFn({ meta, ...file }: FileUpload) {
      // ponytail: queryOptions()+generic TQueryKey isn't assignable to fetchQuery (DataTag/staleTime); spread UseQueryOptions instead
      const data = await queryClient.fetchQuery({ staleTime: 25 * 60 * 1000, ...props })

      return baseMutation.mutateAsync({
        uploadInfo: data,
        defaultMeta: options.defaultMeta ?? {},
        meta,
        ...file,
      })
    },
  })

  return {
    ...mutation,
    isPendingUploadUrl: isPending,
    isUploadUrlError: isError,
    isFetchingUploadUrl: isFetching,
  }
}

export function useUploadMultipleFiles<TQueryKey extends QueryKey = QueryKey>(
  props: UseGetUploadFileUrlProps<TQueryKey>,
  options: UseMutationOptions<unknown, unknown, FileUpload[], unknown> & {
    defaultMeta?: Record<string, string>
    stopOnError?: boolean
  } = {}
) {
  const queryClient = useQueryClient()
  const { isPending, isError, isFetching } = useGetUploadFileUrl(props)

  const [numberOfFilesUploaded, setNumberOfFilesUploaded] = useState(0)
  const [numberOfFiles, setNumberOfFiles] = useState(0)

  const mutation = useMutation({
    ...options,
    mutationFn: async (files: FileUpload[]) => {
      setNumberOfFiles(files.length)

      const batches = chunk(files, 2)
      const errors: string[] = []

      await asyncForEach(batches, async (batch) => {
        // refetch the upload url every 10 minutes just to be safe
        const data = await queryClient.fetchQuery({ ...props, staleTime: 10 * 60 * 1000 })
        if (!data) {
          throw new Error('Url & field missing')
        }

        const promises = batch.map(({ meta, ...file }) =>
          uploadFile({
            ...data,
            ...file,
            meta: { ...(options.defaultMeta ?? {}), ...(meta ?? {}) },
          }).catch((error) => {
            if (options.stopOnError) {
              throw error
            }

            errors.push(getErrorMessage(error))
          })
        )

        await Promise.all(promises)

        setNumberOfFilesUploaded((current) => current + promises.length)

        await delay(100)
      })

      if (errors.length > 0) {
        throw new Error(errors.join(', '))
      }
    },
    onSettled(data, error, variables, onMutateResult, context) {
      setNumberOfFilesUploaded(0)
      setNumberOfFiles(0)
      options?.onSettled?.(data, error, variables, onMutateResult, context)
    },
  })

  return {
    mutation,
    numberOfFiles,
    numberOfFilesUploaded,
    isPendingUploadUrl: isPending,
    isUploadUrlError: isError,
    isFetchingUploadUrl: isFetching,
  }
}
